import React, { useEffect, useState } from 'react';
import { useOrderTypeContext } from '../../Contexts/OrderTypeContext';
import CustomerService from '../../Services/CustomerService';
import { useLoginContext } from '../../Contexts/LoginContext';
import ButtonPrimary from './ButtonPrimary';
import { IMaskInput } from 'react-imask';
import { TbCircleFilled, TbPhone } from 'react-icons/tb';
import { useLayoutContext } from '../../Contexts/LayoutContext';
import { Formik, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import 'react-phone-input-2/lib/style.css'
import { ImSpinner8 } from 'react-icons/im';
import { useCartContext } from '../../Contexts/CartContext';
import { useNavigate } from 'react-router-dom';
import { useStoreContext } from '../../Contexts/StoreContext';
import { useStoreHook } from '../../Hooks/StoreHook';
import { useAddressHook } from '../../Hooks/AddressHook';
import { useTranslation } from 'react-i18next';
import { TokenManager } from '../../Services/TokenManager';
import InputPhone from './InputPhone';

const getValidationSchema = (t: any) =>
    Yup.object().shape({
        first_name: Yup.string()
            .min(2, t("form.validation.invalid_min_characters", {quantity: 2}))
            .required(t('form.validation.required')),
        mobile_phone: Yup.string()
            // .min(11, t("form.validation.invalid_min_characters", {quantity: 11}))
            .min(11, t("login.store_counter.invalid_phone"))
            .required(t('form.validation.required')),
});

interface FormLoginBasicProps {
    onCancel: CallableFunction;
}

const FormLoginBasic = ({onCancel}: FormLoginBasicProps) => {

    const { t } = useTranslation();
    const validationSchema = getValidationSchema(t);
    const { orderType, isDelivery } = useOrderTypeContext();
    const { setUser, setShowLoginBasic, isAuthenticated } = useLoginContext();
    const { setShowCart, currentCart, returnToCheckout, setReturnToCheckout } = useCartContext();
    const { setMainLoading, setMainLoadingIcon, setMainLoadingIconError, setMainLoadingTitle, setMainLoadingMessage, setMainLoadingCloseButton, setShowMainMenu } = useLayoutContext();
    const [loadingLogin, setLoadingLogin] = useState(false);
    const { currentStore, currentAddress } = useStoreContext();
    const { getLinkToHome } = useStoreHook();
    const { handleCreateAddress } = useAddressHook();
    const navigate = useNavigate();

    const initialData = {
        first_name: '',
        mobile_phone: '',
    }

    useEffect(() => {
        if (currentStore && isAuthenticated && currentCart && currentCart.items.length > 0) {
            navigate(`${getLinkToHome()}/checkout`);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    const handleLoginBasic = async (data: any) => {
        const payload = {
            first_name: data.first_name,
            mobile_phone: data.mobile_phone,
        }
        try {
            setLoadingLogin(true);
            const customerService = new CustomerService(orderType);
            const response = await customerService.basicRegister(payload);
            if (response) {
                TokenManager.setToken(response.token);
                setUser(response.profile);
                setShowLoginBasic(false);
                setShowCart(false);
                setShowMainMenu(false);

                if (isDelivery && currentAddress) {
                    handleCreateAddress()
                        .then(() => {})
                        .catch(() => {})
                }

                const storedRedirect = localStorage.getItem('redirect');

                if (returnToCheckout || storedRedirect) {
                    navigate(`${getLinkToHome()}/checkout`);
                    setReturnToCheckout(false);
                    localStorage.removeItem('redirect');
                }

            }
        } catch (error) {
            setLoadingLogin(false);
            setMainLoading(true);
            setMainLoadingIcon(false);
            setMainLoadingIconError(true);
            setMainLoadingTitle(t('errors.default_title'));
            setMainLoadingMessage(t('login.error_try_again_message'));
            setMainLoadingCloseButton(true)
        }
    }

    const isFieldRequired = (schema: any, fieldName: string) => {
        const field = schema.describe().fields[fieldName];
        return field.tests.some((test: any) => test.name === 'required');
    };

    return (
        <>
            <Formik
                initialValues={initialData}
                validationSchema={validationSchema}
                onSubmit={(data) => {
                    handleLoginBasic(data);
                }}
                >
                {({ setFieldValue, values }) => (
                    <Form className="flex flex-col gap-4 justify-between">

                        <div className='h-full grid gap-3 p-4 pt-2 bg-gray-50'>

                            <div className='flex flex-col gap-3'>
                                <div className='w-full text-left'>
                                    <span className='font-semibold text-shopfood-title'>
                                        {t('login.basic.form.first_name.label')}
                                        {isFieldRequired(validationSchema, 'first_name') && (<sup className="text-shopfood-error inline-flex ml-0.5"> <TbCircleFilled size={7} /></sup>)}
                                    </span>
                                    <IMaskInput
                                        id="first_name"
                                        name="first_name"
                                        mask={/^[a-zA-ZÀ-ÿ\s]*$/}
                                        value={values.first_name}
                                        onAccept={(value) => setFieldValue('first_name', value)} // Atualiza o valor no Formik
                                        unmask={true} // true|false|'typed'
                                        autoComplete='on'
                                        autoFocus
                                        placeholder={t('login.basic.form.first_name.placeholder')}
                                        className="relative block w-full rounded border-0 p-2 ring-1 ring-gray-300 placeholder:text-gray-300 placeholder:font-normal font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                        disabled={loadingLogin}
                                    />
                                    <ErrorMessage name="first_name" component="div" className='text-xs text-shopfood-error pt-1' />
                                </div>

                                <div className='w-full text-left'>
                                    <span className='font-semibold text-shopfood-title'>
                                        {t('login.basic.form.mobile_phone.label')}
                                        {isFieldRequired(validationSchema, 'mobile_phone') && (<sup className="text-shopfood-error inline-flex ml-0.5"> <TbCircleFilled size={7} /></sup>)}
                                    </span>
                                    <div className='relative flex items-center justify-between'>
                                        <TbPhone size={20} className='absolute z-10 left-2' />
                                        <InputPhone
                                            id="phone"
                                            value={values.mobile_phone}
                                            onAccept={(value: string) => setFieldValue('mobile_phone', value)}
                                            disabled={loadingLogin}
                                            className="relative block w-full"
                                        />
                                    </div>
                                    <ErrorMessage name="mobile_phone" component="div" className='text-xs text-shopfood-error pt-1' />
                                </div>
                            </div>
                        </div>

                        <div className='flex gap-2 p-4 pt-0'>
                            <ButtonPrimary rounded disabled={loadingLogin} type={'submit'} className='gap-1 w-full' onClick={() => {}}>
                                {loadingLogin && <span><ImSpinner8 className="animate-spin" /></span>}
                                <span>{loadingLogin ? t('login.basic.form.button_submit.loading') : t('login.basic.form.button_submit.label')}</span>
                            </ButtonPrimary>
                        </div>

                    </Form>
                )}
            </Formik>
        </>
    )
}

export default FormLoginBasic;
